import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  JwtPayload,
  LoginDto,
  LoginResponseDto,
  LogoutResponseDto,
  RefreshResponseDto,
  UserDto,
} from '@core/models';
import {
  BehaviorSubject,
  catchError,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../../../environments/environment';

const CLIENT_ID_KEY = 'client_id'; // used to store the tenant NIT on mobile

/** Description of this service...
 * AuthApiService - New API-Based Authentication Service
 *
 * This service handles authentication by communicating with the NestJS backend
 * instead of Firebase. It follows the patterns described in Document #4,
 * Sections 5.1-5.3.
 *
 * Key Responsibilities:
 * 1. Send login credentials to the backend
 * 2. Keep the short-lived access token in memory only
 * 3. Maintain the current user state using BehaviorSubject
 * 4. Provide methods for components to check authentication status
 *
 * This is the active authentication service for the backend-based login flow.
 */

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private readonly apiUrl = `${environment.apiUrl}/auth`;
  private accessToken: string | null = null;
  private refreshInFlight$: Observable<RefreshResponseDto> | null = null;
  private currentUserSubject = new BehaviorSubject<UserDto | null>(null); // holds the current user's profile

  // Observable that other components can subscribe to for user state changes
  public currentUser$: Observable<UserDto | null> = this.currentUserSubject.asObservable();
  private http = inject(HttpClient);

  public async initializeSession(): Promise<void> {
    try {
      await firstValueFrom(this.refreshSession());
      const user = await firstValueFrom(this.getProfile());
      console.log('[AuthApiService] Session rehydrated successfully for:', user.email);
    } catch (error) {
      // Refresh cookie may be missing/expired/revoked; leave user unauthenticated.
      console.warn('[AuthApiService] Session rehydration failed:', error);
      this.clearAuthState();
    }
  }

  /** Authenticates a user with email and password.
   *
   * Flow:
   * 1. Send credentials to POST /auth/login
   * 2. Receive { user, accessToken } from backend
   * 3. Keep access token in memory
   * 4. Emit user to currentUserSubject (notifies all subscribers)
   *
   */

  login(credentials: LoginDto): Observable<LoginResponseDto> {
    return this.http
      .post<LoginResponseDto>(`${this.apiUrl}/login`, credentials, { withCredentials: true })
      .pipe(
        tap((response: LoginResponseDto) => {
          this.setAccessToken(response.accessToken);
          console.log('[AuthApiService] Access token stored in memory');

          this.currentUserSubject.next(response.user);
          console.log('[AuthApiService] Login successful for user:', response.user.email);
        }),
        catchError((error) => {
          this.clearAuthState();
          console.error('[AuthApiService] Login failed:', error);
          return throwError(() => error);
        }),
      );
  }

  refreshSession(): Observable<RefreshResponseDto> {
    return this.http
      .post<RefreshResponseDto>(`${this.apiUrl}/refresh`, {}, { withCredentials: true })
      .pipe(
        tap((response) => {
          this.setAccessToken(response.accessToken);
        }),
      );
  }

  public refreshSessionSingleFlight(): Observable<RefreshResponseDto> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.refreshSession().pipe(
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1),
      );
    }

    return this.refreshInFlight$;
  }

  public getValidAccessTokenForRealtime(): Observable<string> {
    const token = this.getToken();

    if (token && !this.isTokenExpired()) {
      return of(token);
    }

    return this.refreshSessionSingleFlight().pipe(map((response) => response.accessToken));
  }

  logout(): void {
    this.logoutSession().subscribe();
  }

  logoutSession(): Observable<LogoutResponseDto> {
    return this.http
      .post<LogoutResponseDto>(`${this.apiUrl}/logout`, {}, { withCredentials: true })
      .pipe(
        catchError((error) => {
          console.warn(
            '[AuthApiService] Logout endpoint failed, clearing frontend state anyway',
            error,
          );
          return of({ message: 'logout-fallback' } as LogoutResponseDto);
        }),
        finalize(() => {
          this.clearAuthState();
          console.log('[AuthApiService] User logged out');
        }),
      );
  }

  getToken(): string | null {
    return this.accessToken;
  }

  public getTokenPayload(): JwtPayload | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    return this.decodeJwtPayload(token);
  }

  public getRolesFromToken(): string[] {
    const payload = this.getTokenPayload();
    if (!payload || !Array.isArray(payload.roles)) {
      return [];
    }

    return payload.roles;
  }

  public getPermissionsFromToken(): string[] {
    const payload = this.getTokenPayload();
    if (!payload || !Array.isArray(payload.permissions)) {
      return [];
    }

    return payload.permissions;
  }

  public getTokenClientId(): string | null {
    const payload = this.getTokenPayload();
    if (!payload || typeof payload.clientId !== 'string') {
      return null;
    }

    return payload.clientId;
  }

  public getIsAdminFromToken(): boolean {
    const payload = this.getTokenPayload();
    return payload?.isAdmin === true;
  }

  public isTokenExpired(referenceTimeMs = Date.now()): boolean {
    const payload = this.getTokenPayload();
    if (!payload || typeof payload.exp !== 'number') {
      return true;
    }

    const nowInSeconds = Math.floor(referenceTimeMs / 1000);
    return payload.exp <= nowInSeconds;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getCurrentUser(): UserDto | null {
    return this.currentUserSubject.getValue();
  }

  getProfile(): Observable<UserDto> {
    return this.http.get<UserDto>(`${this.apiUrl}/profile`).pipe(
      tap((user: UserDto) => {
        this.currentUserSubject.next(user);
      }),
    );
  }

  setClientId(clientId: string): void {
    localStorage.setItem(CLIENT_ID_KEY, clientId);
    console.log('[AuthApiService] Client ID stored', clientId);
  }

  getClientId(): string | null {
    return localStorage.getItem(CLIENT_ID_KEY);
  }

  private setAccessToken(accessToken: string): void {
    this.accessToken = accessToken;
  }

  private clearAuthState(): void {
    this.accessToken = null;
    localStorage.removeItem('User');
    localStorage.removeItem(CLIENT_ID_KEY);
    this.currentUserSubject.next(null);
  }

  private decodeJwtPayload(token: string): JwtPayload | null {
    // This method only decodes token claims for UX decisions in the frontend.
    // Signature verification is performed exclusively by the backend on API requests.
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('[AuthApiService] Invalid JWT format');
      return null;
    }

    const payloadBase64Url = parts[1];
    const payloadBase64 = this.normalizeBase64Url(payloadBase64Url);

    try {
      const binaryPayload = atob(payloadBase64);
      const payloadBytes = Uint8Array.from(binaryPayload, (char) => char.charCodeAt(0));
      const payloadJson = new TextDecoder().decode(payloadBytes);
      return JSON.parse(payloadJson) as JwtPayload;
    } catch (error) {
      console.warn('[AuthApiService] Failed to decode JWT payload', error);
      return null;
    }
  }

  private normalizeBase64Url(base64Url: string): string {
    const normalizedBase64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const requiredPadding = (4 - (normalizedBase64.length % 4)) % 4;
    return normalizedBase64 + '='.repeat(requiredPadding);
  }
}
